import { useState } from "react";
import { Star } from "lucide-react";
import type { ReviewEntry } from "../mockData";

function Stars({ count, size = 12 }: { count: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} fill={i <= count ? "#EF9F27" : "transparent"}
          style={{ color: i <= count ? "#EF9F27" : "rgba(28,28,28,0.15)" }} />
      ))}
    </div>
  );
}

function RatingOverview({ reviews }: { reviews: ReviewEntry[] }) {
  const avg = reviews.reduce((s, r) => s + r.stars, 0) / reviews.length;
  const breakdown = [5, 4, 3, 2, 1].map((s) => ({
    stars: s,
    count: reviews.filter((r) => r.stars === s).length,
  }));
  const barColor = (s: number) => s >= 5 ? "#EF9F27" : s >= 4 ? "#FAC775" : "#E24B4A";
  return (
    <div className="flex items-start gap-8 mb-6">
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <span className="font-display font-[500]" style={{ fontSize: 30 }}>{avg.toFixed(1)}</span>
        <Stars count={Math.round(avg)} size={14} />
        <span className="text-[10px] text-ink-40">{reviews.length} отзывов</span>
      </div>
      <div className="flex-1 grid gap-1.5">
        {breakdown.map(({ stars, count }) => (
          <div key={stars} className="flex items-center gap-2">
            <span className="text-[10px] text-ink-40 w-4">{stars}★</span>
            <div className="flex-1 h-[4px] rounded-full overflow-hidden" style={{ background: "rgba(28,28,28,0.07)" }}>
              <div className="h-full rounded-full" style={{
                width: `${reviews.length ? (count / reviews.length) * 100 : 0}%`,
                background: barColor(stars),
              }} />
            </div>
            <span className="text-[10px] font-mono text-ink-40 w-4 text-right">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: ReviewEntry }) {
  return (
    <div className="rounded-lg p-[10px_12px] mb-2" style={{ border: "0.5px solid rgba(28,28,28,0.12)" }}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[12px] font-[500]">{review.author}</div>
          <div className="text-[11px] text-ink-40">{review.company}</div>
        </div>
        <div className="text-right">
          <Stars count={review.stars} size={12} />
          <div className="text-[10px] text-ink-40 mt-0.5">{review.date}</div>
        </div>
      </div>
      <div className="flex gap-1 flex-wrap mb-2">
        {[review.format, review.segment].map((tag) => (
          <span key={tag} className="px-[6px] py-[1px] rounded-full text-[10px] text-ink-40"
            style={{ background: "rgba(28,28,28,0.05)" }}>
            {tag}
          </span>
        ))}
      </div>
      <p className="text-[12px] text-ink-60 leading-[1.5]">{review.text}</p>
    </div>
  );
}

export function ReviewsTab({ reviews }: { reviews: ReviewEntry[] }) {
  const [shown, setShown] = useState(3);
  if (reviews.length === 0) {
    return <div className="text-center py-10 text-[13px] text-ink-40">Отзывов пока нет. Станьте первым!</div>;
  }
  return (
    <div>
      <RatingOverview reviews={reviews} />
      <div>
        {reviews.slice(0, shown).map((r) => <ReviewCard key={r.id} review={r} />)}
      </div>
      {shown < reviews.length && (
        <div className="flex justify-center mt-3">
          <button onClick={() => setShown((n) => n + 3)}
            className="h-8 px-5 rounded-pill border border-ink-12 text-[12px] font-display font-semibold hover:border-ink/40">
            Показать ещё
          </button>
        </div>
      )}
    </div>
  );
}
